'use client';

import { useEffect, useState } from 'react';

interface PercentageLoaderProps {
  percentage: number;
  size?: number;
  trackColor?: string;
  progressColor?: string;
  duration?: number;
  trackColorFirst?: string;
  progressColorFirst?: string;
  trackColorSecond?: string;
  progressColorSecond?: string;
  trackColorThird?: string;
  progressColorThird?: string;
  page?: string;
  strokeWidth?: number;
}

export default function PercentageLoader({
  percentage = 0,
  size = 71,
  trackColorFirst = '#26BD6C33',
  progressColorFirst = '#26BD6C',
  trackColorSecond = '#E6983D66',
  progressColorSecond = '#E6983D',
  trackColorThird = '#BB3D3466',
  progressColorThird = '#BB3D34',
  duration = 600,
  page,
  strokeWidth = 4,
}: PercentageLoaderProps) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const startTime = Date.now();
    const startValue = 0;
    const endValue = Math.min(100, Math.max(0, percentage));

    const updateProgress = () => {
      const currentTime = Date.now();
      const elapsedTime = currentTime - startTime;

      if (elapsedTime < duration) {
        const nextProgress = startValue + ((endValue - startValue) * elapsedTime) / duration;
        setProgress(nextProgress);
        animationFrameId = requestAnimationFrame(updateProgress);
      } else {
        setProgress(endValue);
      }
    };

    let animationFrameId = requestAnimationFrame(updateProgress);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [percentage, duration]);

  // SVG parameters
  const radius = size / 2 - 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  const center = size / 2;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="transform -rotate-90"
      >
        {/* Track Circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={
            percentage > 50 ? trackColorFirst : percentage > 25 ? trackColorSecond : trackColorThird
          }
          strokeWidth={strokeWidth}
        />

        {/* Progress Circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={
            percentage > 50
              ? progressColorFirst
              : percentage > 25
                ? progressColorSecond
                : progressColorThird
          }
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
        />
      </svg>

      {/* Percentage Text */}
      {!(page === 'prospectList') && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-text-label font-medium font-rooftopMedium">
            {Math.round(progress)}%
          </span>
        </div>
      )}
    </div>
  );
}
